<script setup>
import { useApiRequest } from '@/composables/useApiRequest'
import { useApi } from '@/utils/api'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'update',
    subject: 'Inventory',
  },
})

const router = useRouter()
const route = useRoute()
const { loading, errors, request } = useApiRequest()

const ingredientId = route.params.id

// ─── Form Data ─────────────────────────────────────────────────────────────────
const form = ref({
  name: '',
  sku: '',
  unit_id: null,
  category_id: null,
  minimum_stock: 0,
  allow_negative_stock: false,
  is_active: true,
})

const ingredient = ref(null)
const formRef = ref()
const pageLoading = ref(true)

// ─── Options ───────────────────────────────────────────────────────────────────
const units = ref([])
const categories = ref([])

// ─── Fetch ─────────────────────────────────────────────────────────────────────
const fetchAll = async () => {
  pageLoading.value = true
  try {
    const [ingredientRes, unitsRes, catsRes] = await Promise.all([
      useApi(`/api/v1/ingredients/${ingredientId}`).get(),
      useApi('/api/v1/units', { query: { per_page: 100 } }).get(),
      useApi('/api/v1/categories', { query: { per_page: 100 } }).get(),
    ])

    const ingredientData = ingredientRes?.data ?? ingredientRes
    const unitsData = unitsRes?.data ?? unitsRes
    const catsData = catsRes?.data ?? catsRes

    ingredient.value = ingredientData
    units.value = unitsData ?? []
    categories.value = catsData ?? []

    form.value = {
      name: ingredient.value.name,
      sku: ingredient.value.sku ?? '',
      unit_id: ingredient.value.unit?.id ?? null,
      category_id: ingredient.value.category?.id ?? null,
      minimum_stock: ingredient.value.minimum_stock,
      allow_negative_stock: ingredient.value.allow_negative_stock,
      is_active: ingredient.value.is_active,
    }
  } finally {
    pageLoading.value = false
  }
}

// ─── Submit ────────────────────────────────────────────────────────────────────
const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid) return

  const res = await request(() =>
    useApi(`/api/v1/ingredients/${ingredientId}`).put({ body: form.value }),
  )

  if (res) {
    router.push({ name: 'inventory-ingredients-id', params: { id: ingredientId } })
  }
}

onMounted(fetchAll)
</script>

<template>
  <div>
    <!-- Back + Title -->
    <VRow class="mb-4">
      <VCol cols="12">
        <VBtn
          variant="text"
          prepend-icon="ri-arrow-left-line"
          :to="{ name: 'inventory-ingredients-id', params: { id: ingredientId } }"
          class="mb-2 px-0"
        >
          Kembali ke Detail
        </VBtn>
        <h4 class="text-h4 font-weight-medium">
          Edit Bahan Baku
        </h4>
        <p v-if="ingredient" class="text-body-2 text-medium-emphasis mb-0">
          {{ ingredient.name }}
        </p>
      </VCol>
    </VRow>

    <!-- Loading Skeleton -->
    <VRow v-if="pageLoading">
      <VCol cols="12" lg="8">
        <VCard>
          <VCardText>
            <VSkeleton type="article" />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Form -->
    <VRow v-else>
      <VCol cols="12" lg="8">
        <VCard>
          <VCardTitle class="pa-6 pb-3 text-h6">Informasi Bahan Baku</VCardTitle>
          <VDivider />

          <VForm ref="formRef" @submit.prevent="submit">
            <VCardText>
              <VRow>
                <!-- Nama -->
                <VCol cols="12" md="8">
                  <VTextField
                    v-model="form.name"
                    label="Nama Bahan Baku *"
                    :rules="[requiredValidator]"
                    :error-messages="errors.name"
                  />
                </VCol>

                <!-- SKU -->
                <VCol cols="12" md="4">
                  <VTextField
                    v-model="form.sku"
                    label="SKU (opsional)"
                    :error-messages="errors.sku"
                    hint="Kode unik bahan baku"
                    persistent-hint
                  />
                </VCol>

                <!-- Satuan -->
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="form.unit_id"
                    :items="units"
                    item-title="name"
                    item-value="id"
                    label="Satuan *"
                    :rules="[requiredValidator]"
                    :error-messages="errors.unit_id"
                  >
                    <template #item="{ props, item }">
                      <VListItem v-bind="props">
                        <template #append>
                          <VChip size="x-small" color="primary" variant="tonal">
                            {{ item.raw.symbol }}
                          </VChip>
                        </template>
                      </VListItem>
                    </template>
                  </VSelect>
                </VCol>

                <!-- Kategori -->
                <VCol cols="12" md="6">
                  <VSelect
                    v-model="form.category_id"
                    :items="categories"
                    item-title="name"
                    item-value="id"
                    label="Kategori (opsional)"
                    clearable
                    :error-messages="errors.category_id"
                  />
                </VCol>

                <!-- Minimum Stok -->
                <VCol cols="12" md="6">
                  <VTextField
                    v-model.number="form.minimum_stock"
                    label="Minimum Stok"
                    type="number"
                    min="0"
                    step="0.01"
                    :error-messages="errors.minimum_stock"
                    hint="Notifikasi muncul jika stok di bawah angka ini"
                    persistent-hint
                  />
                </VCol>

                <!-- Allow Negative Stock -->
                <VCol cols="12" md="6">
                  <div class="mt-2">
                    <VSwitch
                      v-model="form.allow_negative_stock"
                      label="Izinkan Stok Negatif"
                      color="warning"
                      :error-messages="errors.allow_negative_stock"
                    />
                  </div>
                </VCol>

                <!-- Status -->
                <VCol cols="12">
                  <VSwitch
                    v-model="form.is_active"
                    label="Status Aktif"
                    color="success"
                  />
                </VCol>
              </VRow>

              <!-- Read-only info -->
              <VAlert type="info" variant="tonal" density="compact" class="mt-2">
                <strong>Stok saat ini</strong> dan <strong>HPP rata-rata</strong> tidak bisa diubah di sini.
                Gunakan fitur Stok Awal atau Penyesuaian Stok.
              </VAlert>
            </VCardText>

            <VDivider />

            <VCardActions class="pa-4">
              <VSpacer />
              <VBtn
                variant="text"
                :to="{ name: 'inventory-ingredients-id', params: { id: ingredientId } }"
              >
                Batal
              </VBtn>
              <VBtn
                type="submit"
                color="primary"
                :loading="loading"
                prepend-icon="ri-save-line"
              >
                Simpan Perubahan
              </VBtn>
            </VCardActions>
          </VForm>
        </VCard>
      </VCol>

      <!-- Current Stock Info (read-only) -->
      <VCol cols="12" lg="4">
        <VCard v-if="ingredient">
          <VCardTitle class="pa-6 pb-3 text-h6">Info Stok (Read-only)</VCardTitle>
          <VDivider />
          <VCardText>
            <div class="d-flex justify-space-between mb-3">
              <span class="text-medium-emphasis">Stok Saat Ini</span>
              <span
                class="font-weight-semibold"
                :class="ingredient.is_low_stock ? 'text-warning' : ''"
              >
                {{ ingredient.current_stock }} {{ ingredient.unit?.symbol }}
              </span>
            </div>
            <div class="d-flex justify-space-between mb-3">
              <span class="text-medium-emphasis">HPP Rata-rata</span>
              <span class="font-weight-semibold">
                Rp {{ ingredient.average_cost?.toLocaleString('id-ID') }}
              </span>
            </div>
            <div class="d-flex justify-space-between">
              <span class="text-medium-emphasis">Nilai Stok</span>
              <span class="font-weight-semibold">
                Rp {{ ingredient.stock_value?.toLocaleString('id-ID') }}
              </span>
            </div>

            <VDivider class="my-4" />

            <VBtn
              block
              variant="tonal"
              color="info"
              prepend-icon="ri-history-line"
              :to="{ name: 'inventory-ingredients-id', params: { id: ingredientId } }"
            >
              Lihat Riwayat Stok
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
